import { CoachSettingsEntity } from "@/modules/coach/entities/coachSettingsEntity"
import type { ProfessionalProfileInputDto, ProfessionalProfileUpdateDto } from "@/modules/coach/gateway/coachDto"
import { coachKeys } from "@/modules/coach/gateway/coachKeys"
import { useServicesContext } from "@modules/core/services/services.context"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useCurrentCoach = () => {
  const { coachApi } = useServicesContext()

  const { data } = useSuspenseQuery({
    queryKey: coachKeys.all,
    queryFn: async () => {
      const dto = await coachApi.getCurrentUserProfessionalProfile()
      if (!dto) throw new Error("Coach not found")
      return CoachSettingsEntity.fromDto(dto)
    },
    retry: false,
  })

  return { currentCoach: data }
}

export const useCurrentCoachIfExists = () => {
  const { coachApi } = useServicesContext()

  const { data } = useQuery({
    queryKey: coachKeys.all,
    queryFn: async () => {
      const dto = await coachApi.getCurrentUserProfessionalProfile()
      return dto ? CoachSettingsEntity.fromDto(dto) : null
    },
    retry: false,
  })

  return { currentCoach: data ?? null }
}

export const useCreateProfessionalUser = () => {
  const { coachApi } = useServicesContext()
  const queryClient = useQueryClient()

  const createProfessionalUser = useMutation({
    mutationFn: async (input: ProfessionalProfileInputDto) => {
      const dto = await coachApi.createProfessionalUser(input)
      return CoachSettingsEntity.fromDto(dto)
    },
    onSuccess: (entity) => {
      queryClient.setQueryData(coachKeys.details(entity.state.id), entity)
    },
  })

  return createProfessionalUser
}

export const useAttachProgramsToCoach = () =>
  useMutation({
    // TODO: implement when backend endpoint is available
    mutationFn: async (_input: { coachId: string; programIds: string[] }) => {
      // placeholder — no-op until API is ready
    },
  })

export const useUpdateCoachSettings = () => {
  const { coachApi } = useServicesContext()
  const queryClient = useQueryClient()

  const updateCoachSettings = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ProfessionalProfileUpdateDto }) => {
      const dto = await coachApi.updateProfessionalProfile(id, payload)
      return CoachSettingsEntity.fromDto(dto)
    },
    onSuccess: (entity) => {
      queryClient.setQueryData(coachKeys.all, entity)
      if (entity.state.id) queryClient.setQueryData(coachKeys.details(entity.state.id), entity)
    },
  })

  return updateCoachSettings
}
